import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CreditCard,
  Add,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface PaymentMethod {
  id: number;
  stripe_payment_method_id: string;
  type: string;
  is_default: boolean;
  card_brand?: string;
  card_last4?: string;
  card_exp_month?: number;
  card_exp_year?: number;
  created_at: string;
}

interface Subscription {
  id: number;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  trial_end?: string;
}

interface Plan {
  id: number;
  name: string;
  description?: string;
  stripe_price_id: string;
  amount: number;
  currency: string;
  interval: string;
  trial_period_days?: number;
}

interface TestCard {
  card_number: string;
  exp_month: number;
  exp_year: number;
  cvc: string;
  description: string;
}

const PaymentSetup: React.FC = () => {
  const { token } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [testCards, setTestCards] = useState<TestCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedTestCard, setSelectedTestCard] = useState<TestCard | null>(null);

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch payment configuration and test cards
      const configResponse = await fetch('/api/v1/payments/config', { headers });
      const config = await configResponse.json();
      setTestCards(config.test_cards || []);

      // Fetch payment methods
      const paymentMethodsResponse = await fetch('/api/v1/payments/payment-methods', { headers });
      const paymentMethodsData = await paymentMethodsResponse.json();
      setPaymentMethods(paymentMethodsData);

      // Fetch subscriptions
      const subscriptionsResponse = await fetch('/api/v1/payments/subscriptions', { headers });
      const subscriptionsData = await subscriptionsResponse.json();
      setSubscriptions(subscriptionsData);

      // Fetch available plans
      const plansResponse = await fetch('/api/v1/payments/plans', { headers });
      const plansData = await plansResponse.json();
      setPlans(plansData);

    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTestSubscription = async () => {
    if (!selectedPlan || !selectedTestCard) return;

    try {
      setError(null);
      
      // For demo purposes, we'll show how this would work
      // In a real implementation, you'd use Stripe Elements
      const alertMessage = `
Test Subscription Setup:
- Plan: ${selectedPlan.name} ($${selectedPlan.amount}/${selectedPlan.interval})
- Test Card: ${selectedTestCard.description}
- Card Number: ${selectedTestCard.card_number}

In a real implementation, this would:
1. Create a Stripe PaymentMethod using Stripe Elements
2. Call /api/v1/payments/subscriptions to create the subscription
3. Handle 3D Secure authentication if needed
4. Show success/error messages

For now, this is a demonstration of the payment flow.
      `;
      
      alert(alertMessage);
      setDialogOpen(false);
      
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError('Failed to create subscription');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Test Mode Warning */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🧪 Test Mode Active
        </Typography>
        This is a demonstration of the payment system using Stripe's test environment. 
        No real charges will be made. Use the test cards below to simulate different payment scenarios.
      </Alert>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Payment & Billing
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
          disabled={plans.length === 0}
        >
          Start Subscription
        </Button>
      </Box>

      {/* Current Subscriptions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Subscriptions
          </Typography>
          {subscriptions.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Current Period</TableCell>
                    <TableCell>Trial End</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <Chip
                          label={subscription.status}
                          color={getStatusColor(subscription.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                      </TableCell>
                      <TableCell>
                        {subscription.trial_end ? formatDate(subscription.trial_end) : 'No trial'}
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" color="error">
                          Cancel
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              No active subscriptions. Start a subscription to access the wellness check service.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Methods
          </Typography>
          {paymentMethods.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell>Added</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentMethods.map((pm) => (
                    <TableRow key={pm.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CreditCard sx={{ mr: 1 }} />
                          {pm.type}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {pm.card_brand && pm.card_last4 && (
                          <span>
                            {pm.card_brand.toUpperCase()} ****{pm.card_last4} 
                            {pm.card_exp_month && pm.card_exp_year && (
                              <span> ({pm.card_exp_month}/{pm.card_exp_year})</span>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {pm.is_default && <CheckCircle color="success" />}
                      </TableCell>
                      <TableCell>
                        {formatDate(pm.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              No payment methods on file.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Plans
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {plans.map((plan) => (
              <Card key={plan.id} variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    ${plan.amount}
                    <Typography component="span" variant="body2" color="textSecondary">
                      /{plan.interval}
                    </Typography>
                  </Typography>
                  {plan.description && (
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {plan.description}
                    </Typography>
                  )}
                  {plan.trial_period_days && (
                    <Typography variant="body2" color="success.main" gutterBottom>
                      Immediate access • No waiting period
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setSelectedPlan(plan);
                      setDialogOpen(true);
                    }}
                  >
                    Select Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Test Card Information */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Warning color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Test Cards for Development
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Use these test card numbers to simulate different payment scenarios:
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Card Number</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>CVC</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testCards.map((card, index) => (
                  <TableRow key={index}>
                    <TableCell><code>{card.card_number}</code></TableCell>
                    <TableCell>{card.exp_month}/{card.exp_year}</TableCell>
                    <TableCell>{card.cvc}</TableCell>
                    <TableCell>{card.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Subscription Setup Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Subscription</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {selectedPlan.name}
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                ${selectedPlan.amount}/{selectedPlan.interval}
              </Typography>
              {selectedPlan.trial_period_days && (
                <Typography variant="body2" color="success.main" gutterBottom>
                  Immediate access included
                </Typography>
              )}
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Test Card</InputLabel>
            <Select
              value={selectedTestCard?.card_number || ''}
              onChange={(e) => {
                const card = testCards.find(c => c.card_number === e.target.value);
                setSelectedTestCard(card || null);
              }}
            >
              {testCards.map((card, index) => (
                <MenuItem key={index} value={card.card_number}>
                  {card.card_number} - {card.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info">
            This is a demonstration. In a real implementation, you would use Stripe Elements 
            to securely collect card information instead of selecting test cards.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTestSubscription} 
            variant="contained"
            disabled={!selectedPlan || !selectedTestCard}
          >
            Start Subscription (Demo)
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentSetup;
